import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  dialogClasses,
  DialogContent,
  dialogContentClasses,
  DialogTitle as MuiDialogTitle,
  IconButton,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled, useTheme } from "@mui/material/styles";
import {
  CreateSportEquipmentInput, GetSportEquipmentsDocument,
  ImportSportEquipmentsInput, TechnicalCondition,
  useImportSportEquipmentMutation
} from "graphql/graphQlApiHooks";
import EditableTable from "./EditableTable";
import { ImportedSportEquipmentRecord } from "./types";
import { useSnackbar } from "notistack";
import { declOfNum } from "helpers/declination";
import { faker } from "@faker-js/faker";
import { rowValidate } from "./utils";
import {
  useGetSportEquipmentInputVars
} from "../../../../../../../../store/reactiveVarsStores/spor-equipmnts/getSportEquipmentsInput";
import { BootstrapTooltip } from "../../../../../../../../components/BootstrapTooltip";
import dayjs from "dayjs";


const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  padding: theme.spacing(0, 3),
  position: "sticky",
  top: 0,
  display: "flex",
  margin: 0,
  height: 48,
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  zIndex: 1
}));

const Title = styled(Typography)(({ theme }) => ({
  width: "100%",
  textAlign: "left"
})) as typeof Typography;


interface Props {
  open: boolean;
  close: () => void;
  importedSportEquipmentRecords?: ImportedSportEquipmentRecord[];
}

const normalizeCommissioningDate = (value?: string | null) => {
  if (!value) {
    return value;
  }

  const trimmedValue = String(value).trim();

  // Если дата в формате DD.MM.YYYY, конвертируем в YYYY-MM-DD
  const ddMmYyyyMatch = trimmedValue.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (ddMmYyyyMatch) {
    const day = ddMmYyyyMatch[1].padStart(2, "0");
    const month = ddMmYyyyMatch[2].padStart(2, "0");
    const year = ddMmYyyyMatch[3];
    return `${year}-${month}-${day}`;
  }

  return trimmedValue;
};


export const ImportFromExcel = (props: Props) => {
  const { open, close, importedSportEquipmentRecords } = props;
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [records, setRecords] = useState<ImportedSportEquipmentRecord[]>([]);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false); // Флаг инициализации
  const { getInput } = useGetSportEquipmentInputVars();

  function handleCloseModal() {
    close();
    setRecords([]);
    setDisabled(true);
    setSubmitting(false);
    setIsInitialized(false); // Сбрасываем флаг при закрытии
  }

  const [importSportEquipment] = useImportSportEquipmentMutation();

  const onSubmit = () => {
    setSubmitting(true);
    const getImportSportEquipmentsInput = (records: ImportedSportEquipmentRecord[]): CreateSportEquipmentInput[] => {
      // Фильтруем только валидные записи перед импортом
      const validRecords = records.filter((record: ImportedSportEquipmentRecord) => {
        // Проверяем, что дата существует, не пустая и валидна
        // Поддерживаем оба формата: DD.MM.YYYY и YYYY-MM-DD
        const dateStr = record.commissioningDate ? String(record.commissioningDate).trim() : "";
        const hasValidDate = dateStr &&
          record.isCommissioningDateCorrect &&
          (dayjs(dateStr, "DD.MM.YYYY", true).isValid() || 
           dayjs(dateStr, "YYYY-MM-DD", true).isValid());
        
        return (
          record.name &&
          record.sportObject &&
          hasValidDate &&
          record.technicalCondition !== undefined &&
          record.inUse !== undefined
        );
      });

      return validRecords
        .map((record: ImportedSportEquipmentRecord) => {
          const {
            name,
            accountingName,
            inventoryNumber,
            description,
            length,
            width,
            height,
            weight,
            technicalCondition,
            failureReason,
            inUse,
            commissioningDate,
            equipmentType,
            sportKind,
            sportObject,
            sportZone,
            sportSubZone,
            sportCategory
          } = record;

          // Проверяем валидность даты перед отправкой
          // Бэкенд ожидает формат DD.MM.YYYY (русский формат с точками)
          let validCommissioningDate: string | null = null;
          if (commissioningDate && String(commissioningDate).trim()) {
            const trimmedDate = String(commissioningDate).trim();
            
            // Пытаемся распарсить дату в разных форматах
            let date = dayjs(trimmedDate, "DD.MM.YYYY", true); // сначала пробуем DD.MM.YYYY
            
            if (!date.isValid()) {
              // Если не получилось, пробуем YYYY-MM-DD
              date = dayjs(trimmedDate, "YYYY-MM-DD", true);
            }
            
            if (!date.isValid()) {
              // Если дата невалидна, пытаемся нормализовать её
              const normalized = normalizeCommissioningDate(trimmedDate);
              if (normalized && String(normalized).trim()) {
                // Нормализация возвращает YYYY-MM-DD, парсим её
                date = dayjs(String(normalized).trim(), "YYYY-MM-DD", true);
              }
            }
            
            // Проверяем валидность и форматируем в DD.MM.YYYY для бэкенда
            if (date.isValid()) {
              const formattedDate = date.format("DD.MM.YYYY");
              // Дополнительная проверка: убеждаемся, что форматированная дата не содержит NaN
              if (formattedDate && !formattedDate.includes("NaN") && /^\d{2}\.\d{2}\.\d{4}$/.test(formattedDate)) {
                validCommissioningDate = formattedDate;
              } else {
                console.error("Invalid formatted date (contains NaN or wrong format):", formattedDate, "from:", commissioningDate);
                return null;
              }
            } else {
              // Если дата всё ещё невалидна, возвращаем null для фильтрации
              console.error("Invalid commissioning date:", commissioningDate);
              return null;
            }
          } else {
            // Если дата пустая или содержит только пробелы, возвращаем null
            console.error("Empty or whitespace-only commissioning date:", commissioningDate);
            return null;
          }
          
          // Финальная проверка перед возвратом
          if (!validCommissioningDate || typeof validCommissioningDate !== "string") {
            console.error("Commissioning date is not a valid string:", validCommissioningDate);
            return null;
          }

          return {
            name: name!,
            ...(accountingName && {
              accountingName
            }),
            ...(inventoryNumber && {
              inventoryNumber
            }),
            ...(description && {
              description
            }),
            ...(length !== null && length !== undefined && {
              length
            }),
            ...(width !== null && width !== undefined && {
              width
            }),
            ...(height !== null && height !== undefined && {
              height
            }),
            ...(weight !== null && weight !== undefined && {
              weight
            }),
            commissioningDate: validCommissioningDate,
            ...(technicalCondition !== undefined && {
              technicalCondition: technicalCondition as TechnicalCondition
            }),
            ...(failureReason && {
              failureReason
            }),
            ...(inUse !== undefined && {
              inUse
            }),
            sportObjectId: sportObject!.id,
            ...(sportZone?.id && {
              sportZoneId: sportZone.id
            }),
            ...(sportSubZone?.id && {
              sportSubZoneId: sportSubZone.id
            }),
            ...(sportCategory?.id && {
              sportCategoryId: sportCategory.id
            }),
            ...(sportKind?.id && {
              sportKindId: sportKind.id
            }),
            ...(equipmentType?.id && {
              equipmentTypeId: equipmentType.id
            })
          } as CreateSportEquipmentInput;
        })
        .filter((input): input is CreateSportEquipmentInput => input !== null);
    };

    const createEquipmentInputs: CreateSportEquipmentInput[] = getImportSportEquipmentsInput(records);

    if (createEquipmentInputs.length === 0) {
      enqueueSnackbar("Нет валидных записей для импорта", { variant: "warning" });
      setSubmitting(false);
      return;
    }

    const importSportEquipmentsInput: ImportSportEquipmentsInput = {
      createEquipmentInputs: createEquipmentInputs
    };

    importSportEquipment({
      variables: {
        input: importSportEquipmentsInput
      },
      refetchQueries: [
        {
          query: GetSportEquipmentsDocument,
          fetchPolicy: "network-only",
          variables: {
            input: getInput()
          }
        }
      ]
    }).then((res) => {
      const validCount = createEquipmentInputs.length;
      const skippedCount = records.length - validCount;
      
      handleCloseModal();
      
      if (skippedCount > 0) {
        enqueueSnackbar(
          `Успешно добавлено ${validCount} ${declOfNum(validCount, ["единица", "единицы", "единиц"])} оборудования. Пропущено ${skippedCount} ${declOfNum(skippedCount, ["запись", "записи", "записей"])} с ошибками.`,
          { variant: "success", autoHideDuration: 6000 }
        );
      } else {
        enqueueSnackbar(
          `Успешно добавлено ${validCount} ${declOfNum(validCount, ["единица", "единицы", "единиц"])} оборудования`,
          { variant: "success" }
        );
      }
    }).catch(({ message }) => {
      enqueueSnackbar(
        message.replace("Unexpected error value: ", "").replace(/"/g, ""),
        {
          variant: "error",
          autoHideDuration: 6000
        }
      );
    }).finally(() => {
      setSubmitting(false);
    });

  };

  useEffect(() => {
    const errs = [...records]?.map((data: any) => rowValidate(data));
    setDisabled([...errs].filter(error => Object.keys(error).length > 1).length > 0);
  }, [records]);

  // Инициализация данных только один раз при открытии диалога
  useEffect(() => {
    if (open && importedSportEquipmentRecords && !isInitialized && records.length === 0) {
      const initialData: ImportedSportEquipmentRecord[] = [...importedSportEquipmentRecords]
        .map(item => ({
          ...item,
          id: faker.datatype.uuid(),
          commissioningDate: normalizeCommissioningDate(item?.commissioningDate as string | undefined),
          isCommissioningDateCorrect:
            item?.commissioningDate
              ? item?.isCommissioningDateCorrect !== undefined
                ? item.isCommissioningDateCorrect
                : true
              : item?.isCommissioningDateCorrect,
        })) || [];
      setRecords(initialData);
      setIsInitialized(true);
    }
  }, [open, importedSportEquipmentRecords, isInitialized, records.length]);

  // Закрываем диалог, если после инициализации не осталось строк
  useEffect(() => {
    if (open && isInitialized && records.length === 0) {
      handleCloseModal();
    }
  }, [open, isInitialized, records.length]);

  // Сбрасываем флаг инициализации при закрытии диалога
  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);


  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth
        sx={{
          [`& .${dialogClasses.paper}`]: {
            width: "100%",
            height: "100%",
            [theme.breakpoints.down("md")]: {
              margin: 0,
              borderRadius: 0,
              alignSelf: "end",
              maxHeight: "100%"
            },
            [`& .${dialogContentClasses.root}`]: {
              padding: 0
            }
          }
        }}
      >

        <DialogTitle>
          <Title variant={"h5"} component={"div"}> 
            Загрузка оборудования из файла
            {records.length > 0 && (
              <Typography variant="caption" sx={{ ml: 2, color: "text.secondary", fontWeight: 400 }}>
                ({records.length} {declOfNum(records.length, ["запись", "записи", "записей"])})
              </Typography>
            )}
          </Title>
          <BootstrapTooltip title={"Закрыть"}>
            <IconButton size={"small"} onClick={handleCloseModal}> <CloseIcon fontSize={"inherit"} /> </IconButton>
          </BootstrapTooltip>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          {records?.length > 0 ? (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <EditableTable
                onDataChange={(data) => setRecords(data)}
                data={records}
                onSubmit={onSubmit}
                disabled={disabled}
                submitting={submitting}
              />
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Загрузка данных...
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
